import classNames from 'classnames';
import { twMerge } from 'tailwind-merge';
import { getStorage } from './browsers';
import { formatDateTime } from './datetime';
import {
    DATE_YMD_2,
    DAY_OF_WEEK,
    ekDomain,
    NON_RENTAL_TYPES,
    ROUTES
} from '@/configs/constants';
import { NonRentalDatesValues } from '@/types/bike';
import { t, TFunction } from 'i18next';
import { DELETE_STATUS, LICENSE_TYPE_OPTION, LicenseTypeOption, PROMOTION_TYPE, ROLE_USER, RoleUserType } from '@/types/enums';
import { isPathEk } from './routes';
import { formatNumber } from './formatNumber';
import { URL_CONFIG } from '@/configs/environment';

export function cn(...inputs: classNames.ArgumentArray) {
    return twMerge(classNames(inputs));
}

export function getAvatarFallbackName(fullname?: string) {
    if (!fullname) return '';
    const names = fullname.trim().split(' ');

    if (names.length === 1) {
        // Trường hợp chỉ có một từ
        return `${names[0].charAt(0).toUpperCase()}${names[0]
            .charAt(1)
            .toUpperCase()}`;
    } else if (names.length > 1) {
        // Lấy ký tự đầu tiên của tên và họ
        const firstInitial = names[0].charAt(0).toUpperCase();
        const lastInitial = names[names.length - 1].charAt(0).toUpperCase();
        return `${firstInitial}${lastInitial}`;
    }

    return '';
}

export function formatDateToDDMMYYYY(isoString: string) {
    const date = new Date(isoString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Tháng bắt đầu từ 0
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
}

// #region Check roles
export function isRoles(arrRoleId: number[]): boolean {
    let roleId: number;

    if (isPathEk(window.location.pathname)) {
        roleId = getStorage('profile_ek', true)?.roleId;
    } else {
        roleId = getStorage('profile_gas', true)?.roleId;
    }

    if (arrRoleId.includes(roleId)) {
        return true;
    }

    return false;
}
export function isRoleEndUser(): boolean {
    let roleId: RoleUserType | undefined = undefined;

    if (isPathEk(window.location.pathname)) {
        roleId = getStorage('profile_ek', true)?.roleId;
    } else {
        roleId = getStorage('profile_gas', true)?.roleId;
    }

    if (
        roleId === ROLE_USER.RENTAL_BIKE_OWNER_EK ||
        roleId === ROLE_USER.RENTAL_BIKE_OWNER_RD
    ) {
        return true;
    }

    return false;
}

export const isEmptyRole = (roleId: number): boolean => !roleId;

export const isAdmin = (roleId: number): boolean => {
    if (!roleId) return false;

    if (
        roleId === ROLE_USER.SYSTEM_ADMIN ||
        roleId === ROLE_USER.BEST_BIKE_ADMIN
    ) {
        return true;
    }

    return false;
};

export const isOwner = (roleId: number): boolean => {
    if (!roleId) return false;

    if (
        roleId === ROLE_USER.FRANCHISE_OWNER_PRM ||
        roleId === ROLE_USER.FRANCHISE_OWNER_STD ||
        roleId === ROLE_USER.SHARE_BIKE_OWNER ||
        roleId === ROLE_USER.HOTEL_BIKE_OWNER
    ) {
        return true;
    }

    return false;
};

export const isAdminOrOwner = (roleId: number): boolean => {
    if (!roleId) return false;

    if (isAdmin(roleId) || isOwner(roleId)) {
        return true;
    }

    return false;
};

export const isEnduser = (roleId: number): boolean => {
    if (!roleId) return false;

    if (
        roleId === ROLE_USER.RENTAL_BIKE_OWNER_RD ||
        roleId === ROLE_USER.RENTAL_BIKE_OWNER_EK
    ) {
        return true;
    }

    return false;
};
// #endregion

export const convertArrayToString = (arr: any[]): string[] => {
    return arr.map(item => String(item));
};

export const isFalsyValue = (value) => {
    return (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '')
    );
};

export const scrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: 'smooth'
    });
};

export const generateNonRentalDescription = (
    nonRentalDates: NonRentalDatesValues,
    nonRentalDatesDetails: string[],
    t: TFunction
): string => {
    const nonRentalType = nonRentalDates?.nonRentalType;
    const startDate = nonRentalDates?.startDate;
    const endDate = nonRentalDates?.endDate;
    const startTimeOfMonth = nonRentalDates?.startTimeOfMonth;
    const endTimeOfMonth = nonRentalDates?.endTimeOfMonth;
    const selectedDaysOfWeekly = {
        sunday: nonRentalDates?.sunday ?? false,
        monday: nonRentalDates?.monday ?? false,
        tuesday: nonRentalDates?.tuesday ?? false,
        wednesday: nonRentalDates?.wednesday ?? false,
        thursday: nonRentalDates?.thursday ?? false,
        friday: nonRentalDates?.friday ?? false,
        saturday: nonRentalDates?.saturday ?? false
    };
    const nonRentalDate = nonRentalDatesDetails ?? [];

    // day
    if (
        nonRentalType === NON_RENTAL_TYPES.Day &&
        nonRentalDate &&
        nonRentalDate.length > 0
    ) {
        const formattedDates = nonRentalDate
            .map(date => formatDateTime(date, DATE_YMD_2))
            .join(', ');

        return t('sentences.non_rental_day', {
            dates: formattedDates
        });
    }

    // weekly
    if (nonRentalType === NON_RENTAL_TYPES.Weekly && startDate && endDate) {
        const selectedDays = Object.entries(DAY_OF_WEEK)
            .filter(([_, { key }]) => selectedDaysOfWeekly?.[key])
            .map(([_, { key }]) => t(`words_title.${key}`))
            .join(', ');

        if (!selectedDays) return '';

        return t('sentences.non_rental_weekly', {
            days: selectedDays,
            startDate: formatDateTime(startDate, DATE_YMD_2),
            endDate: formatDateTime(endDate, DATE_YMD_2)
        });
    }

    // monthly
    if (
        nonRentalType === NON_RENTAL_TYPES.Monthly &&
        startDate &&
        endDate &&
        startTimeOfMonth &&
        endTimeOfMonth
    ) {
        return t('sentences.non_rental_monthly', {
            startTimeOfMonth,
            endTimeOfMonth,
            startDate: formatDateTime(startDate, DATE_YMD_2),
            endDate: formatDateTime(endDate, DATE_YMD_2)
        });
    }

    return '--';
};

// #region Show hours reservation reception
export const getReservationReception = (
    hour: number | string | null | undefined,
    t: TFunction
) => {
    if (!hour) {
        return '--';
    }

    const hourNumber = Number(hour);

    if (isNaN(hourNumber)) {
        return '--';
    }

    return hourNumber === 1 ?
        `${t('sentences.hour_1')}` :
        `${t('sentences.hours_number', { number: hourNumber })}`;
};

// #region Show Engine size
export const getEngineSize = (engineSizeName: string, t: TFunction) => {
    if (!engineSizeName) {
        return '--';
    }

    return t(`words_title.${engineSizeName}`);
};

export const convertDateTime = (isoString: string, useLocalTime: boolean = false): { day: string, date: string, time: string } => {
    // 2024-12-24T08:31:25.576Z ========> { day: 'monday', date: '2024/12/24', time: '18:31' }

    if (!isoString) {
        return {
            day: '',
            date: '',
            time: ''
        };
    }

    const [day, date, time] = formatDateTime(
        isoString,
        'dddd, YYYY/MM/DD HH:mm',
        {
            useLocalTime
        }
    )?.split(/, | /) || ['', '', ''];

    return { day: day.toLowerCase(), date, time };
};

// #region Get link location detail enduser
export const getLickDetailLocation = (
    isEk: boolean,
    locationId: number | string | undefined
): string => {
    return isEk ?
        `/${ekDomain}/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${locationId}` :
        `/${ROUTES.locationList.href}/${ROUTES.locationDetailEnduser.href}/${locationId}`;
};

// #region Server Messages
export const getServerMessage = (errorCode: string, errorParams: any): any => {
    if (typeof errorCode === 'string' && errorCode?.length > 0) {
        const params = (errorParams && typeof errorParams === 'object' && Object.keys(errorParams)?.length > 0) ? errorParams : {};

        // Xử lý đặc biệt cho ERROR_CANNOT_INACTIVE_ACCOUNT_BECAUSE_BOOKING_EXIST
        if (
            (errorCode === 'ERROR_CANNOT_INACTIVE_ACCOUNT_BECAUSE_BOOKING_EXIST' || errorCode === 'ERROR_DELETE_HAS_BOOKING_NOT_COMPLETE') &&
            Array.isArray(errorParams?.reservationCode)
        ) {
            // Nối các phần tử mảng bằng <br /> để ngắt dòng
            params.reservationCode = errorParams.reservationCode.join('<br />');
        }

        return {
            i18nKey: `server_messages.${errorCode}`,
            values: params
        };
    }

    return {
        i18nKey: 'server_messages.API_RESPONSE_NO_MESSAGE',
        values: {}
    };
};
// export const getServerMessage = (
//     errorCode: string,
//     errorParams: any,
//     t: TFunction
// ): string => {
//     if (typeof errorCode === 'string' && errorCode?.length > 0) {
//         const params = errorParams &&
//             typeof errorParams === 'object' &&
//             Object.keys(errorParams)?.length > 0 ?
//             errorParams :
//             {};
//         return `${t(`server_messages.${errorCode}`, params)}`;
//     }

//     return `${t('server_messages.API_RESPONSE_NO_MESSAGE')}`;
// };
// #endregion Server Messages

export const showDiscount = (
    promotionType: number,
    discountRate: number | string
): string => {
    switch (promotionType) {
        case PROMOTION_TYPE.PERCENTAGE: {
            return `${discountRate}%`;
        }
        case PROMOTION_TYPE.MONEY: {
            const formatNumberDiscountRate = formatNumber(discountRate);
            return formatNumberDiscountRate ?
                `${t('currency.currency_symbol')}${formatNumberDiscountRate}` :
                '';
        }
    }
    return '';
};

// #region Replace spaces
export const replaceSpaces = (str: string): string => {
    return str?.replace(/\s+/g, '');
};

export const convertDistance = (meters: number): string => {
    if (meters < 1000) {
        return `${meters}m`;
    } else {
        return `${(meters / 1000).toFixed(2)}km`;
    }
};
// Get url item
export const getLinkOrderDetail = (id: string | number): string => {
    if (!id) return '';

    return isPathEk(window.location.pathname) ?
        `/${ekDomain}/${ROUTES.reservationManagement.hrefEK}/${ROUTES.reservationDetail.hrefEK}/${id}` :
        `${ROUTES.reservationManagement.href}/${ROUTES.reservationDetail.href}/${id}`;
};

export const getLinkLocationDetail = (id: string | number): string => {
    if (!id) return '';

    return isPathEk(window.location.pathname) ?
        `ek/${ROUTES.locationManagement.hrefEK}/${ROUTES.locationEdit.hrefEK}/${id}` :
        `${ROUTES.locationManagement.href}/${ROUTES.locationEdit.href}/${id}`;
};

export const getLinkBikeDetail = (id: string | number) => {
    if (!id) return '';

    return isPathEk(window.location.pathname) ?
        `/${ekDomain}/${ROUTES.bikeManagement.hrefEK}/${ROUTES.bikeEdit.hrefEK}/${id}` :
        `${ROUTES.bikeManagement.href}/${ROUTES.bikeEdit.href}/${id}`;
};
export const getLinkAccountDetail = (id: string | number) => {
    if (!id) return '';

    return isPathEk(window.location.pathname) ?
        `/${ekDomain}${ROUTES.userManagement.hrefEK}/${ROUTES.userEdit.hrefEK}/${id}` :
        `${ROUTES.userManagement.href}/${ROUTES.userEdit.href}/${id}`;
};

// #region if value is a valid LicenseTypeOption
export const isLicenseType = (value: unknown): boolean => {
    return typeof value === 'number' && Object.values(LICENSE_TYPE_OPTION).includes(value as LicenseTypeOption);
};

// #region Delete text japan in address
export const deleteJapanText = (value: string): string => {
    return value
        .replace(/^Japan, ?\s*/i, '')
        .replace(/^日本、?\s*/i, '');
};

// #region Link Home
export const linkHomePage = (): string => {
    const pathname = window.location.pathname;
    const isEk = isPathEk(pathname);
    return isEk ?
        `${URL_CONFIG.DOMAIN_WORDPRESS.url}${ekDomain}` :
        `${URL_CONFIG.DOMAIN_WORDPRESS.url}`;
};

// #region Check Status delete
export const isDeleted = (status: number) => {
    const statusNumber = Number(status);
    return (statusNumber === DELETE_STATUS.IS_DELETE || statusNumber === DELETE_STATUS.IS_DELETE_DISABLE);
};

// #endregion Check Status delete
